/**
 * Settings dropdown styled like Microphone Quality (`app-settings-sound-select-*`).
 * Native `<select>` widgets add their own padding, so settings pages use this instead.
 */

import { useMemo } from 'react';
import { Select, Portal, createListCollection } from '@ark-ui/react';

export interface SettingsSelectItem {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SettingsSelectProps {
  value: string;
  items: SettingsSelectItem[];
  onValueChange: (value: string) => void;
  disabled?: boolean;
  /**
   * id of an element that names this control (passed as `aria-labelledby`).
   * Do not set `id` on Select.Trigger — Ark resolves the anchor by its own id.
   */
  labelId?: string;
  ariaLabel?: string;
  className?: string;
}

export function SettingsSelect({
  value,
  items,
  onValueChange,
  disabled = false,
  labelId,
  ariaLabel,
  className,
}: SettingsSelectProps) {
  const collection = useMemo(
    () =>
      createListCollection({
        items,
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
        isItemDisabled: (item) => item.disabled === true,
      }),
    [items],
  );

  return (
    <Select.Root
      className={className ? `settings-select ${className}` : 'settings-select'}
      collection={collection}
      value={[value]}
      disabled={disabled}
      onValueChange={(details) => {
        const next = details.value[0];
        if (next) onValueChange(next);
      }}
      positioning={{
        placement: 'bottom-start',
        sameWidth: true,
        strategy: 'fixed',
      }}
    >
      <Select.Control className="app-settings-sound-select-control">
        <Select.Trigger
          className="app-settings-sound-select-trigger"
          {...(labelId ? { 'aria-labelledby': labelId } : {})}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        >
          <Select.ValueText className="app-settings-sound-select-value" />
          <Select.Indicator className="app-settings-sound-select-indicator" aria-hidden>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Select.Indicator>
        </Select.Trigger>
      </Select.Control>
      <Portal>
        <Select.Positioner className="app-settings-sound-select-positioner">
          <Select.Content className="app-settings-sound-select-content">
            <Select.List className="app-settings-sound-select-list">
              {items.map((item) => (
                <Select.Item key={item.value} item={item} className="app-settings-sound-select-item">
                  <Select.ItemText className="app-settings-sound-select-item-text">{item.label}</Select.ItemText>
                  <Select.ItemIndicator className="app-settings-sound-select-item-indicator">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path
                        d="M11.5 3.5L5.25 9.75L2.5 7"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.List>
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
}
